import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { ROUTES } from '../../lib/routes';
import { RoleVerificationService } from '../../services/roleVerificationService';
import type { User } from '../../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: User['type'] | User['type'][];
  redirectTo?: string;
  allowPendingPayment?: boolean;
}

export default function ProtectedRoute({
  children,
  requiredRole,
  redirectTo = ROUTES.LOGIN,
  allowPendingPayment = false
}: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading, logout } = useAuthStore();
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);
  const [serverDenied, setServerDenied] = useState(false);
  const readyRef = useRef(false);
  const verifiedRef = useRef(false);

  useEffect(() => {
    if (readyRef.current) return;
    const timer = setTimeout(() => {
      readyRef.current = true;
      setIsReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!user || !isAuthenticated || verifiedRef.current) return;
    verifiedRef.current = true;

    const verify = async () => {
      try {
        const expectedRole = requiredRole
          ? (Array.isArray(requiredRole) ? requiredRole[0] : requiredRole)
          : undefined;

        const result = await RoleVerificationService.verifyUserRole(
          user.id,
          expectedRole,
          allowPendingPayment
        );

        if (!result.isValid) {
          if (result.actualStatus === 'suspended' || result.actualStatus === 'rejected') {
            logout();
          }
          setServerDenied(true);
        }
      } catch {
        // Network error — don't block access, client-side check is still in place
      }
    };

    verify();
  }, [user, isAuthenticated, requiredRole, allowPendingPayment, logout]);

  if (!isReady || isLoading) {
    return null;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to={redirectTo} replace />;
  }

  const status = user.status;
  if (status && status !== 'active') {
    if (status === 'pending_payment') {
      if (!allowPendingPayment) {
        return <Navigate to={ROUTES.VISITOR_DASHBOARD} replace />;
      }
    } else if (status === 'pending') {
      return <Navigate to={ROUTES.PENDING_ACCOUNT} replace />;
    } else if (status === 'suspended' || status === 'rejected') {
      return <Navigate to={ROUTES.LOGIN} replace />;
    } else {
      return <Navigate to={ROUTES.LOGIN} replace />;
    }
  }

  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!allowedRoles.includes(user.type)) {
      return <Navigate to={ROUTES.FORBIDDEN} replace />;
    }
  }

  if (serverDenied) {
    return <Navigate to={ROUTES.FORBIDDEN} replace />;
  }

  return <>{children}</>;
}
